import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { newsService, countdownService, authService } from '../services/api';
import { NewsItem, SiteCountdownData } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { useSiteConfig } from '../context/SiteConfigContext';
import { resolvePublicMediaUrl } from '../utils/newsContent';
import { defaultHomeShortcuts } from '../admin/adminSiteConfigData';
import HeroCountdown from '../components/HeroCountdown';
import '../styles/Home.css';

const defaultHeroImage = `${process.env.PUBLIC_URL || ''}/assets/img/ridersoficarus.jpg`;

const SHORTCUT_LABEL_KEYS: Record<string, 'home.shortcutGuides' | 'home.shortcutMounts' | 'home.shortcutClasses' | 'home.shortcutRankings'> = {
    guides: 'home.shortcutGuides',
    mounts: 'home.shortcutMounts',
    classes: 'home.shortcutClasses',
    rankings: 'home.shortcutRankings',
};

const Home: React.FC = () => {
    const [latestNews, setLatestNews] = useState<NewsItem[]>([]);
    const [countdown, setCountdown] = useState<SiteCountdownData | null>(null);
    const { t, locale } = useLanguage();
    const { config } = useSiteConfig();
    const dateLocale = locale === 'pt-BR' ? 'pt-BR' : 'en-US';
    const discordUrl = config.links?.discord || 'https://discord.gg/ps9smnSu9j';
    const isAuthenticated = authService.isAuthenticated();

    const heroSubtitle = config.home?.heroSubtitle ?? t('home.rideTheSkies');
    const heroTitle = config.home?.heroTitle ?? t('home.heroTitle');
    const heroTitleHighlight = config.home?.heroTitleHighlight ?? t('home.heroTitleHighlight');
    const heroDescription = config.home?.heroDescription ?? t('home.heroDesc');
    const heroBackground = resolvePublicMediaUrl(config.home?.heroBackgroundImage) || defaultHeroImage;
    const countdownLabel = countdown
        ? (locale === 'pt-BR' ? countdown.labelPtBr : countdown.labelEn) || countdown.labelPtBr
        : undefined;

    const shortcuts = useMemo(() => {
        const base = config.home?.shortcuts?.length ? config.home.shortcuts : defaultHomeShortcuts;
        return base.map(item => ({
            ...item,
            to: item.to || defaultHomeShortcuts.find(d => d.id === item.id)?.to || '/',
            icon: item.icon || defaultHomeShortcuts.find(d => d.id === item.id)?.icon || 'ri-link',
            label: SHORTCUT_LABEL_KEYS[item.id] ? t(SHORTCUT_LABEL_KEYS[item.id]) : item.id,
            imageUrl: resolvePublicMediaUrl(item.imageUrl),
        }));
    }, [config.home?.shortcuts, t]);

    useEffect(() => {
        const loadNews = async () => {
            try {
                const data = await newsService.getAllNews();
                setLatestNews(data.slice(0, 3));
            } catch (err) {
                console.error('Erro ao carregar últimas notícias', err);
            }
        };
        loadNews();
    }, []);

    useEffect(() => {
        const loadCountdown = async () => {
            try {
                const data = await countdownService.getCountdown();
                setCountdown(data);
            } catch (err) {
                console.error('Erro ao carregar countdown', err);
            }
        };
        loadCountdown();
    }, []);

    const formatDate = (value?: string) => {
        if (!value) return '';
        const d = new Date(value);
        return d.toLocaleDateString(dateLocale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        }).toUpperCase();
    };

    const scrollToNews = useCallback(() => {
        document.getElementById('home-news')?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    const newsImage = (item: NewsItem) =>
        resolvePublicMediaUrl(item.imageUrl) || defaultHeroImage;

    return (
        <div className="home">
            <header
                className="hero"
                style={{ backgroundImage: `url(${heroBackground})` }}
            >
                <div className="hero-overlay" />
                <div className="container hero-inner">
                    <div className="hero-text">
                        <span className="hero-subtitle">{heroSubtitle}</span>
                        <h1>
                            {heroTitle}
                            <span className="highlight">{heroTitleHighlight}</span>
                        </h1>
                        <p>{heroDescription}</p>
                        <div className="hero-buttons">
                            <Link to="/download" className="btn btn-xl btn-primary">
                                <i className="ri-download-cloud-2-line" aria-hidden="true" />
                                {t('home.playNow')}
                            </Link>
                            <Link to="/game" className="btn btn-xl btn-ghost-light">
                                {t('home.learnMore')}
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="hero-dock">
                    <div className="container hero-dock-inner">
                        <div className="hero-social">
                            <a href={discordUrl} target="_blank" rel="noopener noreferrer" aria-label="Discord">
                                <i className="ri-discord-line" />
                            </a>
                        </div>
                        <button type="button" className="hero-scroll" onClick={scrollToNews}>
                            <span className="hero-scroll-icon" aria-hidden="true">
                                <i className="ri-arrow-down-s-line" />
                            </span>
                            {t('home.scrollDown')}
                        </button>
                        <div className="hero-dock-right">
                            {countdown?.targetDateUtc && (
                                <HeroCountdown
                                    compact
                                    targetIso={countdown.targetDateUtc}
                                    label={countdownLabel}
                                    textColor={countdown.textColor}
                                    labelFontSizePx={countdown.labelFontSizePx}
                                    valueFontSizePx={countdown.valueFontSizePx}
                                />
                            )}
                            {!isAuthenticated && (
                                <Link to="/register" className="hero-subscribe">
                                    {t('home.subscribeNow')}
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <section className="shortcuts-section">
                <div className="container">
                    <h2 className="section-label">{t('home.exploreWorld')}</h2>
                    <div className="shortcuts-grid">
                        {shortcuts.map((item) => (
                            <Link
                                key={item.id}
                                to={item.to}
                                className={`shortcut-card${item.imageUrl ? ' shortcut-card--image' : ''}`}
                            >
                                {item.imageUrl ? (
                                    <span className="shortcut-media">
                                        <img src={item.imageUrl} alt="" loading="lazy" />
                                    </span>
                                ) : (
                                    <span className="shortcut-icon">
                                        <i className={item.icon} aria-hidden="true" />
                                    </span>
                                )}
                                <span className="shortcut-label">{item.label}</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <section className="news-section" id="home-news">
                <div className="container">
                    <div className="news-header">
                        <h2 className="section-title-light">
                            {t('home.newsEvents')}
                        </h2>
                        <Link to="/news" className="view-all-btn-light">
                            {t('home.viewAll')} <i className="ri-arrow-right-line" aria-hidden="true" />
                        </Link>
                    </div>
                    <div className="news-grid">
                        {latestNews.map((item) => (
                            <article key={item.id} className="news-card-v2">
                                <Link to={`/news/${item.id}`} className="news-card-media">
                                    <img
                                        src={newsImage(item)}
                                        alt=""
                                        loading="lazy"
                                    />
                                </Link>
                                <div className="news-card-body">
                                    <time className="news-date-v2">{formatDate(item.createdAt)}</time>
                                    <h3>
                                        <Link to={`/news/${item.id}`}>{item.title}</Link>
                                    </h3>
                                    <p>{item.summary || (item.content ? `${item.content.replace(/<[^>]+>/g, '').slice(0, 100)}...` : '')}</p>
                                </div>
                            </article>
                        ))}
                        {latestNews.length === 0 && (
                            <p className="news-empty">{t('home.noNews')}</p>
                        )}
                    </div>
                </div>
            </section>

            <section className="cta-section">
                <div className="container cta-inner">
                    <h2>{t('home.ctaTitle')}</h2>
                    <p>{t('home.ctaDesc')}</p>
                    <div className="hero-buttons">
                        <Link to="/download" className="btn btn-xl btn-primary">
                            {t('home.downloadGame')}
                        </Link>
                        {!isAuthenticated && (
                            <Link to="/register" className="btn btn-xl btn-outline-dark">
                                {t('home.createAccount')}
                            </Link>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
